import { useNotification, useTranslate } from "@refinedev/core";
import {
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Typography
} from "antd";
import { Rule } from "antd/es/form";
import { useCallback, useMemo, useEffect } from "react";

import { AutoComplete } from "../../../components/inputs/autocomplete";
import { SelectField } from "../../../components/inputs/select/select";
import { getWorldcheckVerificationStatusOptionLabel } from "../../../components/inputs/select/statusSelect.constants";
import { User } from "../../../interfaces/users";
import { ButtonListFooterModalSM } from "../../../pages/components/buttonsList";
import { confirmModalUnsavedChangesProps } from "../../../pages/components/constants";
import { companyRegistrationNumberRules } from "../../../pages/payment-request/addCompany.constants";
import {
  trimFormFieldValue,
  websiteRules,
} from "../../../utils/form.constants";
import { useIsAdminRole } from "../../../utils/hooks/useIdentityAdmin.hook";
import { useNotificationOkCancel } from "../../../utils/hooks/useNotificationOkCancel.hook";
import { useCreateCustomer } from "../../../utils/hooks/useUpdateCustomer.hook";
import { VerificationStatus } from "../../../utils/hooks/useWorldCheckVerification.hook";
import { buttonStyle } from "../../pages/auth/components/styles";
import { addressRules, nameRules } from "./const/rules";
import { DownOutlined } from "@ant-design/icons";

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  formSubmit: (data: any) => void;
  closeModal: () => void;
  refetchList: () => void;
}
export const NewCustomerModalCreate = ({
  modalProps,
  formProps,
  closeModal,
  refetchList,
}: IProps) => {
  const { open: openNotificationSucces } = useNotification();
  const translate = useTranslate();
  const isAdminRole = useIsAdminRole();

  const { add: addCustomer, isLoading } = useCreateCustomer();

  const onSubmit = useCallback(
    async (formValues: User) => {
      const data = {
        email: formValues.email,
        fullName: formValues.fullName,
        address: formValues.address,
        website: formValues.website,
        regNo: formValues.worldCheckVerification.regNo,
        country: formValues.worldCheckVerification.companyCountry,
        worldCheckVerification: {
          status: formValues.worldCheckVerification.status,
        },
      };
      const isSuccess = await addCustomer(data);

      if (isSuccess) {
        if (openNotificationSucces) {
          openNotificationSucces({
            key: "success_transaction_confirm",
            message: "Create customer successfully!",
            type: "success",
          });
        }
        formProps.form?.resetFields();
        closeModal();
        refetchList();
      }
    },
    [
      addCustomer,
      closeModal,
      formProps.form,
      openNotificationSucces,
      refetchList
    ]
  );

  const handleSubmit = useCallback(() => {
    formProps.form?.submit();
  }, [formProps.form]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: closeModal,
  });

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(formProps.form?.getFieldsValue()).some(
      (value) => value !== undefined
    );
    if (formIsNotEmpty) {
      openNotification();
    } else {
      closeModal();
    }
  }, [closeModal, formProps.form, openNotification]);

  const afterClose = useCallback(() => {
    formProps.form?.resetFields();
  }, [formProps]);

  const emailRules: Rule[] = useMemo(
    () => [
      { required: true, validateTrigger: "onSubmit" },
      {
        type: "email",
        message: translate(
          "pages.register.errors.validEmail",
          "Invalid email address"
        ),
        validateTrigger: "onSubmit",
      },
    ],
    [translate]
  );

  useEffect(() => {
    if (formProps?.form && modalProps?.open) {
      setTimeout(() => {
        formProps?.form?.setFieldsValue({
          worldCheckVerification: {
            status: VerificationStatus.REQUIRED,
          },
        });
      }, 100);
    }
  }, [formProps?.form, modalProps?.open]);

  return (
    <Modal
      {...modalProps}
      title="Add new customer"
      className="smartModalSM"
      footer={ButtonListFooterModalSM(handleSubmit, onModalCancel, isLoading)}
      afterClose={afterClose}
      onCancel={onModalCancel}
    >
      {contextHolder}
      <Form
        {...formProps}
        layout="vertical"
        onFinish={onSubmit}
        disabled={isLoading}
      >
        <Form.Item label="Email" name="email" rules={emailRules}>
          <Input
            size="large"
            placeholder={translate("pages.register.fields.email", "Email")}
            style={buttonStyle}
          />
        </Form.Item>
        <Form.Item label="Full Name/ Company Name" name="fullName" rules={nameRules}>
          <Input placeholder="Full Name/ Company Name" style={buttonStyle} size="large" />
        </Form.Item>
        <Form.Item
          label="Reg. No (TIN, VAT)"
          name={["worldCheckVerification", "regNo"]}
          rules={companyRegistrationNumberRules}
        >
          <Input
            placeholder="Reg. No (TIN, VAT)"
            style={{ ...buttonStyle }}
            size="large"
          />
        </Form.Item>
        <Form.Item
          label="Country of incorporation"
          name={["worldCheckVerification", "companyCountry"]}
          rules={[
            {
              required: true,
              message: "Country of incorporation is required",
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <AutoComplete
            resource="api/v1/worldCheck/countries"
            mapItem={(item: { value: string; label: string }) => ({
              value: item.label,
              key: item.value,
              label: <Typography.Text>{item.label}</Typography.Text>,
              data: item,
            })}
            notFoundPlaceholder="Start by typing country name"
          >
            <Input
              placeholder="Country of incorporation"
              size="large"
              suffix={<DownOutlined />}
            />
          </AutoComplete>
        </Form.Item>
        <Form.Item label="Company's Address" name="address" rules={addressRules}>
          <Input placeholder="Address" style={buttonStyle} size="large" />
        </Form.Item>
        <Form.Item label="Website" name="website" rules={websiteRules}>
          <Input
            size="large"
            placeholder={translate(
              "pages.register.fields.website",
              "Website"
            )}
            onBlur={trimFormFieldValue("website", formProps?.form)}
            style={buttonStyle}
          />
        </Form.Item>
        {isAdminRole && (
          <>
            <Typography
              style={{ textAlign: "left", fontWeight: 500, fontSize: 16 }}
            >
              World Check verification status
            </Typography>
            <SelectField
              name={["worldCheckVerification", "status"]}
              rules={[{ required: true, message: "World Check verification status is required", validateTrigger: "onSubmit" }]}
              getOptionLabel={getWorldcheckVerificationStatusOptionLabel}
              items={Object.values(VerificationStatus)}
            />
          </>
        )}
      </Form>
    </Modal>
  );
};